import { useState, useRef } from 'react';
import { Plus, X, Upload, Download, Loader2, FileSpreadsheet, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';

const INC_TYPES = ['NEAR_MISS', 'FAC', 'MTC', 'RWC', 'LTI', 'FATALITY', 'PROPERTY_DAMAGE', 'ENVIRONMENTAL', 'VEHICLE_ACCIDENT'];
const SEVERITIES = ['NEGLIGIBLE', 'MINOR', 'MAJOR', 'CRITICAL'];
const CATEGORIES = ['FALL_FROM_HEIGHT', 'STRUCK_BY', 'CAUGHT_IN_BETWEEN', 'ELECTRICAL_CONTACT', 'SLIP_TRIP_FALL', 'BURN_SCALD', 'MANUAL_HANDLING', 'FALLING_OBJECT', 'VEHICLE_INCIDENT', 'CONFINED_SPACE', 'HOT_WORK_FIRE', 'CHEMICAL_EXPOSURE', 'HEAT_STRESS', 'SCAFFOLDING_FAILURE', 'CRANE_INCIDENT'];
const AREAS = ['Unit 1', 'Pipe Rack PR-01', 'Tank Farm', 'Substation', 'Control Room', 'Fabrication Yard', 'Laydown Area', 'Admin Block'];

function FormField({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

const inputClass = "w-full rounded-lg px-3 py-2 text-sm outline-none transition-all";
const inputStyle = { backgroundColor: 'var(--hover-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' };

export function IncidentCreateForm({ projectId, onCreated, onClose }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    incidentDate: new Date().toISOString().split('T')[0],
    incidentTime: '',
    incidentType: '',
    incidentSeverity: 'MINOR',
    incidentCategory: '',
    area: '',
    briefDescription: '',
    detailedDescription: '',
    activityBeingPerformed: '',
    immediateAction: '',
    injuredPersons: [],
  });
  const [showInjured, setShowInjured] = useState(false);
  const [injured, setInjured] = useState({ personName: '', trade: '', injuryType: '', bodyPart: '', treatmentProvided: 'FIRST_AID', lostWorkdays: 0, restrictedWorkdays: 0 });

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const addInjured = () => {
    if (!injured.personName) return toast.error('Person name required');
    setForm(prev => ({ ...prev, injuredPersons: [...prev.injuredPersons, { ...injured }] }));
    setInjured({ personName: '', trade: '', injuryType: '', bodyPart: '', treatmentProvided: 'FIRST_AID', lostWorkdays: 0, restrictedWorkdays: 0 });
    setShowInjured(false);
  };

  const removeInjured = (idx) => {
    setForm(prev => ({ ...prev, injuredPersons: prev.injuredPersons.filter((_, i) => i !== idx) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.incidentType) return toast.error('Incident Type is required');
    if (!form.briefDescription) return toast.error('Brief Description is required');
    setSaving(true);
    try {
      await api.post(`/v1/projects/${projectId}/hse/incidents`, form);
      toast.success('Incident created');
      onCreated?.();
    } catch {
      toast.error('Failed to create incident');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-10 bg-black/40" onClick={onClose}>
      <div className="w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-xl shadow-2xl" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} onClick={e => e.stopPropagation()} data-testid="incident-create-form">
        <div className="sticky top-0 z-10 flex items-center justify-between p-4" style={{ backgroundColor: 'var(--card-bg)', borderBottom: '1px solid var(--border-color)' }}>
          <h2 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>Report New Incident</h2>
          <button onClick={onClose} className="p-1 rounded-lg hover:opacity-70" style={{ color: 'var(--text-muted)' }}><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Incident Date" required>
              <input type="date" value={form.incidentDate} onChange={e => set('incidentDate', e.target.value)} className={inputClass} style={inputStyle} />
            </FormField>
            <FormField label="Incident Time">
              <input type="time" value={form.incidentTime} onChange={e => set('incidentTime', e.target.value)} className={inputClass} style={inputStyle} />
            </FormField>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <FormField label="Incident Type" required>
              <select value={form.incidentType} onChange={e => set('incidentType', e.target.value)} className={inputClass} style={inputStyle}>
                <option value="">Select...</option>
                {INC_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
              </select>
            </FormField>
            <FormField label="Severity" required>
              <select value={form.incidentSeverity} onChange={e => set('incidentSeverity', e.target.value)} className={inputClass} style={inputStyle}>
                {SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </FormField>
            <FormField label="Category">
              <select value={form.incidentCategory} onChange={e => set('incidentCategory', e.target.value)} className={inputClass} style={inputStyle}>
                <option value="">Select...</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
              </select>
            </FormField>
          </div>
          <FormField label="Area">
            <select value={form.area} onChange={e => set('area', e.target.value)} className={inputClass} style={inputStyle}>
              <option value="">Select...</option>
              {AREAS.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </FormField>
          <FormField label="Brief Description" required>
            <textarea value={form.briefDescription} onChange={e => set('briefDescription', e.target.value)} rows={2} className={inputClass} style={inputStyle} placeholder="Brief description of the incident..." />
          </FormField>
          <FormField label="Detailed Description">
            <textarea value={form.detailedDescription} onChange={e => set('detailedDescription', e.target.value)} rows={3} className={inputClass} style={inputStyle} placeholder="Full details..." />
          </FormField>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Activity Being Performed">
              <input value={form.activityBeingPerformed} onChange={e => set('activityBeingPerformed', e.target.value)} className={inputClass} style={inputStyle} placeholder="e.g. Pipe spool erection" />
            </FormField>
            <FormField label="Immediate Action Taken">
              <input value={form.immediateAction} onChange={e => set('immediateAction', e.target.value)} className={inputClass} style={inputStyle} placeholder="e.g. Area secured, first aid provided" />
            </FormField>
          </div>

          {/* Injured Persons */}
          <div className="rounded-lg p-3" style={{ backgroundColor: 'var(--hover-bg)', border: '1px solid var(--border-color)' }}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>Injured Persons ({form.injuredPersons.length})</span>
              <button type="button" onClick={() => setShowInjured(!showInjured)} className="text-[10px] px-2 py-1 rounded-md" style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}>
                <Plus className="w-3 h-3 inline mr-0.5" />Add
              </button>
            </div>
            {form.injuredPersons.map((p, i) => (
              <div key={i} className="flex items-center gap-2 text-xs py-1" style={{ borderBottom: '1px solid var(--border-color)' }}>
                <span style={{ color: 'var(--text-primary)' }}>{p.personName} ({p.trade}) — {p.injuryType} / {p.bodyPart}</span>
                <button type="button" onClick={() => removeInjured(i)} className="ml-auto text-red-400 hover:text-red-600"><X className="w-3 h-3" /></button>
              </div>
            ))}
            {showInjured && (
              <div className="mt-2 space-y-2 p-2 rounded-lg" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                <div className="grid grid-cols-2 gap-2">
                  <input placeholder="Person Name *" value={injured.personName} onChange={e => setInjured(p => ({ ...p, personName: e.target.value }))} className={`${inputClass} text-xs`} style={inputStyle} />
                  <input placeholder="Trade" value={injured.trade} onChange={e => setInjured(p => ({ ...p, trade: e.target.value }))} className={`${inputClass} text-xs`} style={inputStyle} />
                  <input placeholder="Injury Type" value={injured.injuryType} onChange={e => setInjured(p => ({ ...p, injuryType: e.target.value }))} className={`${inputClass} text-xs`} style={inputStyle} />
                  <input placeholder="Body Part" value={injured.bodyPart} onChange={e => setInjured(p => ({ ...p, bodyPart: e.target.value }))} className={`${inputClass} text-xs`} style={inputStyle} />
                  <select value={injured.treatmentProvided} onChange={e => setInjured(p => ({ ...p, treatmentProvided: e.target.value }))} className={`${inputClass} text-xs`} style={inputStyle}>
                    <option value="FIRST_AID">First Aid</option>
                    <option value="MEDICAL_TREATMENT">Medical Treatment</option>
                    <option value="HOSPITALIZED">Hospitalized</option>
                  </select>
                  <div className="flex gap-1">
                    <input type="number" min="0" placeholder="Lost days" value={injured.lostWorkdays || ''} onChange={e => setInjured(p => ({ ...p, lostWorkdays: +e.target.value }))} className={`${inputClass} text-xs flex-1`} style={inputStyle} />
                    <input type="number" min="0" placeholder="Restricted" value={injured.restrictedWorkdays || ''} onChange={e => setInjured(p => ({ ...p, restrictedWorkdays: +e.target.value }))} className={`${inputClass} text-xs flex-1`} style={inputStyle} />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={addInjured} className="text-[10px] px-3 py-1 rounded-md bg-green-600 text-white">Add Person</button>
                  <button type="button" onClick={() => setShowInjured(false)} className="text-[10px] px-3 py-1 rounded-md" style={{ color: 'var(--text-muted)' }}>Cancel</button>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>Cancel</Button>
            <Button type="submit" size="sm" disabled={saving} data-testid="incident-submit-btn">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
              Create Incident
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}


export function BulkImportPanel({ projectId, type = 'incidents', onImported }) {
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);
  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  const handleDownloadTemplate = () => {
    window.open(`${API_BASE}/api/v1/projects/${projectId}/hse/${type}/template`, '_blank');
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`/v1/projects/${projectId}/hse/${type}/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
      if (res.data?.imported > 0) {
        toast.success(`Imported ${res.data.imported} ${type}`);
        onImported?.();
      }
      if (res.data?.errors?.length > 0) {
        toast.warning(`${res.data.errors.length} rows had errors`);
      }
    } catch {
      toast.error('Import failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} data-testid={`bulk-import-${type}`}>
      <div className="flex items-center gap-3 mb-3">
        <FileSpreadsheet className="w-4 h-4" style={{ color: 'var(--accent-text)' }} />
        <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Bulk Import {type === 'incidents' ? 'Incidents' : 'Observations'}</span>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="outline" size="sm" onClick={handleDownloadTemplate} data-testid={`download-template-${type}`}>
          <Download className="w-3.5 h-3.5 mr-1" /> Download Template
        </Button>
        <label className="cursor-pointer">
          <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={handleUpload} className="hidden" />
          <Button variant="outline" size="sm" asChild disabled={uploading}>
            <span>
              {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
              Upload Excel
            </span>
          </Button>
        </label>
      </div>
      {result && (
        <div className="mt-3 space-y-2">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-500" />
            <span className="text-xs" style={{ color: 'var(--text-primary)' }}>Imported: <strong>{result.imported}</strong> of {result.totalRows} rows</span>
          </div>
          {result.errors?.length > 0 && (
            <div className="rounded-lg p-2 max-h-32 overflow-y-auto" style={{ backgroundColor: '#EF444410', border: '1px solid #EF444420' }}>
              <div className="flex items-center gap-1 mb-1">
                <AlertTriangle className="w-3 h-3 text-red-500" />
                <span className="text-[10px] font-semibold text-red-500">{result.errors.length} Errors</span>
              </div>
              {result.errors.slice(0, 10).map((err, i) => (
                <div key={i} className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Row {err.row}: {err.error}</div>
              ))}
              {result.errors.length > 10 && <div className="text-[10px]" style={{ color: 'var(--text-disabled)' }}>...and {result.errors.length - 10} more</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
